import { useTranslation } from 'react-i18next'
import { Alert } from '@/components/ui/Alert'
import { SkeletonList } from '@/components/ui/Skeleton'
import { useTopics } from '../hooks/useTopics'
import type { TopicNode } from '../types'

export interface InterestPickerProps {
  /** Identifiants des topics actuellement choisis — contrôlé par l'appelant. */
  readonly selected: string[]
  readonly onChange: (topicIds: string[]) => void
  /** Bornes de sélection (onboarding : 1 à 5 — SPEC §6.2 n'impose que 1 à 20 côté serveur). */
  readonly min?: number
  readonly max?: number
}

/**
 * Aplatit l'arbre en topics réellement sélectionnables : niveau 1 (thème) et
 * 2 (sous-thème) seulement — jamais une discipline entière (niveau 0), pour
 * la même raison que `SetInterestsRequest` le refuse côté serveur (SPEC §6.2).
 */
function selectableNodes(nodes: TopicNode[]): TopicNode[] {
  return nodes.flatMap((node) => {
    const own = node.level >= 1 ? [node] : []
    return [...own, ...selectableNodes(node.children)]
  })
}

/** Libellé dans la langue courante, repli sur `fr` (SPEC §3 — jamais de texte en dur). */
function labelFor(node: TopicNode, language: string): string {
  return node.labels[language] ?? node.labels.fr ?? node.slug
}

/**
 * Sélecteur de matières d'intérêt (SPEC §6.1, §6.2) : coche entre `min` et
 * `max` thèmes/sous-thèmes parmi l'arbre `GET /topics`, groupés par
 * discipline. Composant contrôlé et pur — la persistance
 * (`PUT /me/interests`) et le caractère passable de l'étape restent à la
 * charge de l'appelant (l'étape d'onboarding qui l'embarque).
 */
export function InterestPicker({ selected, onChange, min = 1, max = 5 }: InterestPickerProps) {
  const { t, i18n } = useTranslation()
  const { data: topics, isLoading, isError } = useTopics()

  const toggle = (topicId: string) => {
    if (selected.includes(topicId)) {
      onChange(selected.filter((id) => id !== topicId))
      return
    }
    if (selected.length >= max) return
    onChange([...selected, topicId])
  }

  const disciplines = topics?.filter((node) => node.level === 0) ?? []

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-on-surface-variant">{t('practice.interests.hint', { min, max })}</p>

      {isError && <Alert variant="error">{t('practice.interests.load_error')}</Alert>}
      {isLoading && <SkeletonList rows={4} rowClassName="h-11" />}

      {topics && disciplines.length > 0 && (
        <div className="flex flex-col gap-4">
          {disciplines.map((discipline) => {
            const nodes = selectableNodes(discipline.children)
            if (nodes.length === 0) return null

            return (
              <div key={discipline.id}>
                <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-on-surface-variant">
                  {labelFor(discipline, i18n.language)}
                </p>
                <div
                  role="group"
                  aria-label={labelFor(discipline, i18n.language)}
                  className="flex flex-wrap gap-2"
                >
                  {nodes.map((node) => {
                    const isSelected = selected.includes(node.id)
                    const isDisabled = !isSelected && selected.length >= max

                    return (
                      <button
                        key={node.id}
                        type="button"
                        aria-pressed={isSelected}
                        disabled={isDisabled}
                        onClick={() => toggle(node.id)}
                        className={[
                          'min-h-[44px] rounded-full border-2 px-4 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40',
                          isSelected
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-transparent bg-surface-low text-on-surface hover:bg-surface-container',
                        ].join(' ')}
                      >
                        {labelFor(node, i18n.language)}
                      </button>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      )}

      <p role="status" aria-live="polite" className="text-xs text-on-surface-variant">
        {t('practice.interests.count', { count: selected.length, max })}
      </p>
    </div>
  )
}
